import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../components/Card';
import { KpiCard } from '../components/KpiCard';
import { Pill } from '../components/Pill';
import { EarningsChart } from '../components/EarningsChart';
import { Icon } from '../components/Icons';
import { btnGhost } from '../components/buttons';
import { adminApi } from '../api/admin';
import { fmtNum, fmtUSD } from '../lib/format';

export function DashboardScreen() {
  const dashboard = useQuery({ queryKey: ['admin', 'dashboard'], queryFn: adminApi.dashboard });
  const series = useQuery({ queryKey: ['admin', 'series', 12], queryFn: () => adminApi.earningsSeries(12) });
  const activity = useQuery({ queryKey: ['admin', 'activity', 10], queryFn: () => adminApi.activity(10) });

  const d = dashboard.data;
  const monthDelta = d && d.earnings.lastMonthCommissionCents > 0
    ? ((d.earnings.thisMonthCommissionCents - d.earnings.lastMonthCommissionCents) / d.earnings.lastMonthCommissionCents) * 100
    : null;

  return (
    <div style={{ padding: '24px 28px 60px', display: 'grid', gap: 20, maxWidth: 1280 }}>
      {/* Hero row — top KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        <KpiCard
          label="Active creators"
          value={d?.creators.active ?? 0}
          sublabel={d ? `${d.creators.total} total · ${d.creators.suspended} suspended` : ''}
          accent
        />
        <KpiCard
          label="Active subscribers"
          value={d?.subscribers.activeAcrossAllCodes ?? 0}
          sublabel={d ? `${fmtNum(d.subscribers.totalAttributedAllTime)} attributed all-time` : ''}
        />
        <KpiCard
          label="This month · creator commissions"
          value={d?.earnings.thisMonthCommissionCents ?? 0}
          format={fmtUSD}
          decimals={2}
          delta={monthDelta}
          sublabel={d ? `vs. ${fmtUSD(d.earnings.lastMonthCommissionCents)} last month` : ''}
        />
        <KpiCard
          label="Lifetime payouts"
          value={d?.payouts.paidLifetimeCents ?? 0}
          format={fmtUSD}
          decimals={0}
          sublabel={d ? `${fmtUSD(d.payouts.paidThisMonthCents)} this month` : ''}
        />
      </div>

      {/* Pending actions row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        <PendingCard
          label="Pending code requests"
          count={d?.pending.codeApprovals ?? 0}
          subline="Creators waiting for approval"
          to="/codes"
          ctaLabel="Review queue"
          icon={<Icon.Plus />}
        />
        <PendingCard
          label="Scheduled payouts"
          count={d?.pending.payoutsCount ?? 0}
          subline={d ? `${fmtUSD(d.pending.payoutsAmountCents)} ready to send` : ''}
          to="/payouts"
          ctaLabel="Process"
          icon={<Icon.Money />}
        />
        <PendingCard
          label="New creators this month"
          count={d?.creators.newThisMonth ?? 0}
          subline="Joined since the 1st"
          to="/creators"
          ctaLabel="View creators"
          icon={<Icon.Users />}
        />
      </div>

      {/* Chart + activity */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)', gap: 14 }} className="snapmon-chart-row">
        <Card padding={20} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>
              Commission paid · all creators
            </div>
            <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 2 }}>
              Last 12 months · sum of every creator's 10% cut
            </div>
          </div>
          {series.data && series.data.length > 0 ? (
            <EarningsChart
              data={series.data.map((p) => ({ label: p.label, amountCents: p.commissionCents }))}
              height={260}
            />
          ) : (
            <div style={{ height: 260, display: 'grid', placeItems: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
              {series.isLoading ? 'Loading…' : 'No earnings recorded yet'}
            </div>
          )}
        </Card>

        <Card padding={0} style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '18px 20px 8px', borderBottom: '1px solid var(--line)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Recent activity</div>
            <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 2 }}>Last 10 events across the platform</div>
          </div>
          {(activity.data ?? []).length === 0 ? (
            <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 12 }}>
              {activity.isLoading ? 'Loading…' : 'No activity yet.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {(activity.data ?? []).map((a, i) => (
                <div
                  key={a.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 20px',
                    borderBottom: i === (activity.data ?? []).length - 1 ? 'none' : '1px solid var(--line)',
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: eventColor(a.eventType),
                      flexShrink: 0,
                      boxShadow: `0 0 0 3px color-mix(in oklab, ${eventColor(a.eventType)} 20%, transparent)`,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {eventLabel(a.eventType)}{' '}
                      <span style={{ color: 'var(--ink-3)' }}>
                        · {a.creator?.name ?? 'unknown'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', display: 'flex', gap: 8, marginTop: 2 }}>
                      {a.code && <span className="mono">{a.code}</span>}
                      <span>{new Date(a.occurredAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                    </div>
                  </div>
                  {a.amountCents != null && (
                    <div className="num" style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)' }}>
                      +{fmtUSD(a.amountCents)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function PendingCard({
  label,
  count,
  subline,
  to,
  ctaLabel,
  icon,
}: {
  label: string;
  count: number;
  subline: string;
  to: string;
  ctaLabel: string;
  icon: React.ReactNode;
}) {
  return (
    <Card padding={18} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div
          style={{
            fontSize: 12,
            color: 'var(--ink-3)',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontWeight: 500,
          }}
        >
          {label}
        </div>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--r-sm)',
            background: 'var(--brand-soft)',
            color: 'var(--brand-ink)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {icon}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <div className="num" style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 600, letterSpacing: '-0.03em' }}>
          {count}
        </div>
        {count > 0 && <Pill tone="warn" size="sm">action needed</Pill>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{subline}</div>
      <Link to={to} style={{ ...btnGhost, textDecoration: 'none', justifyContent: 'center' }}>
        {ctaLabel}
        <Icon.Arrow />
      </Link>
    </Card>
  );
}

function eventColor(t: string): string {
  if (t === 'new_sub' || t === 'renewal' || t === 'payout_paid' || t === 'code_approved') return 'var(--good)';
  if (t === 'churn' || t === 'code_rejected') return 'var(--bad)';
  return 'var(--warn)';
}

function eventLabel(t: string): string {
  const map: Record<string, string> = {
    new_sub: 'New sub',
    renewal: 'Renewal',
    churn: 'Churn',
    code_approved: 'Code approved',
    code_rejected: 'Code rejected',
    payout_paid: 'Payout paid',
  };
  return map[t] ?? t;
}
